import type { DateTime } from 'luxon';
import type {
  ConfirmCampaignImportBatchResponseDtoOutput,
  CreateCampaignImportBatchDto,
  CreateCampaignImportBatchResponseDto,
} from '@/generated/korza-api-client/types.gen';
import {
  REQUIRED_IMPORT_FIELDS,
  suggestMapping,
  type FieldSuggestion,
  type ImportField,
} from '@/lib/campaign-import/column-mapping';
import {
  detectDateFormat,
  type DateFormatId,
} from '@/lib/campaign-import/date-parse';
import type { CsvDataRow } from '@/lib/campaign-import/read-csv-file';
import {
  revalidateImportRow,
  validateImportRows,
  type ImportRowContext,
  type ImportRowValues,
  type RowValidationState,
} from '@/lib/campaign-import/row-validation';

export type WizardStep = 'file' | 'mapping' | 'review' | 'confirm' | 'result';

/** Column index of the CSV chosen for each field. */
export type ColumnMapping = Partial<Record<ImportField, number>>;

export interface LoadedFile {
  fileName: string;
  headers: string[];
  rows: CsvDataRow[];
}

export type SubmitFailureReason = 'network' | 'server' | 'conflict';

export type Submission =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'failed'; reason: SubmitFailureReason }
  | { status: 'done'; result: CreateCampaignImportBatchResponseDto };

/** Scheduling (confirming) the batch the import created. Separate from `Submission`: the batch exists either way. */
export type ScheduleState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'failed' }
  | { status: 'done'; result: ConfirmCampaignImportBatchResponseDtoOutput };

export interface WizardState {
  step: WizardStep;
  file: LoadedFile | null;
  suggestions: FieldSuggestion[];
  mapping: ColumnMapping;
  storeWebsiteDomain: string;
  dateFormat: DateFormatId;
  /** True once the user picked the format, so remapping columns does not override it. */
  dateFormatForced: boolean;
  timezone: string;
  /** Cells the user corrected, by row index. They survive going back and remapping. */
  edits: Record<number, Partial<ImportRowValues>>;
  validation: RowValidationState | null;
  skipInvalid: boolean;
  submission: Submission;
  schedule: ScheduleState;
}

export type WizardAction =
  | {
      type: 'fileLoaded';
      fileName: string;
      headers: string[];
      rows: CsvDataRow[];
      storeWebsiteDomain: string;
      timezone: string;
    }
  | { type: 'mappingChanged'; field: ImportField; column: number | null }
  | { type: 'dateFormatChanged'; format: DateFormatId; now: DateTime }
  | { type: 'timezoneChanged'; timezone: string; now: DateTime }
  | { type: 'confirmMapping'; now: DateTime }
  | {
      type: 'cellEdited';
      rowIndex: number;
      field: keyof ImportRowValues;
      value: string;
      now: DateTime;
    }
  | { type: 'skipInvalidChanged'; value: boolean }
  | { type: 'advanceToConfirm' }
  | { type: 'goBack' }
  | { type: 'submitStarted' }
  | { type: 'submitSucceeded'; result: CreateCampaignImportBatchResponseDto }
  | { type: 'submitFailed'; reason: SubmitFailureReason }
  | { type: 'scheduleStarted' }
  | {
      type: 'scheduleSucceeded';
      result: ConfirmCampaignImportBatchResponseDtoOutput;
    }
  | { type: 'scheduleFailed' }
  | { type: 'reset' };

export type MappingBlocker =
  | { type: 'missingFields'; fields: ImportField[] }
  | { type: 'websiteDomainNotConfigured' };

const DEFAULT_FORMAT_WITH_TIME: DateFormatId = 'dd/MM/yyyy HH:mm';
const DEFAULT_FORMAT_DATE_ONLY: DateFormatId = 'dd/MM/yyyy';
const SUGGESTION_SAMPLE_SIZE = 50;

export function createInitialWizardState(): WizardState {
  return {
    step: 'file',
    file: null,
    suggestions: [],
    mapping: {},
    storeWebsiteDomain: '',
    dateFormat: DEFAULT_FORMAT_WITH_TIME,
    dateFormatForced: false,
    timezone: '',
    edits: {},
    validation: null,
    skipInvalid: false,
    submission: { status: 'idle' },
    schedule: { status: 'idle' },
  };
}

function cellOf(row: CsvDataRow, column: number | undefined): string {
  return column === undefined ? '' : (row.cells[column] ?? '');
}

function rowValuesOf(
  row: CsvDataRow,
  index: number,
  mapping: ColumnMapping,
  edits: WizardState['edits']
): ImportRowValues {
  return {
    name: cellOf(row, mapping.name),
    launchDate: cellOf(row, mapping.launchDate),
    launchTime: cellOf(row, mapping.launchTime),
    notificationTitle: cellOf(row, mapping.notificationTitle),
    notificationBody: cellOf(row, mapping.notificationBody),
    notificationLink: cellOf(row, mapping.notificationLink),
    utmCampaign: cellOf(row, mapping.utmCampaign),
    labels: cellOf(row, mapping.labels),
    ...edits[index],
  };
}

function detectFormat(file: LoadedFile, mapping: ColumnMapping): DateFormatId {
  const hasTimeColumn = mapping.launchTime !== undefined;
  const detected = detectDateFormat(
    file.rows.map((row) => cellOf(row, mapping.launchDate)),
    hasTimeColumn
  );
  return (
    detected ??
    (hasTimeColumn ? DEFAULT_FORMAT_DATE_ONLY : DEFAULT_FORMAT_WITH_TIME)
  );
}

function contextOf(state: WizardState, now: DateTime): ImportRowContext {
  return {
    storeWebsiteDomain: state.storeWebsiteDomain,
    now,
    dateFormat: state.dateFormat,
    timezone: state.timezone,
  };
}

function revalidateAll(state: WizardState, now: DateTime): WizardState {
  if (!state.file) return state;
  return {
    ...state,
    validation: validateImportRows(
      state.file.rows.map((row, index) =>
        rowValuesOf(row, index, state.mapping, state.edits)
      ),
      contextOf(state, now)
    ),
  };
}

export function getMappingBlockers(state: WizardState): MappingBlocker[] {
  const blockers: MappingBlocker[] = [];

  const missing = REQUIRED_IMPORT_FIELDS.filter(
    (field) => state.mapping[field] === undefined
  );
  if (missing.length > 0)
    blockers.push({ type: 'missingFields', fields: missing });
  if (state.storeWebsiteDomain.trim() === '') {
    blockers.push({ type: 'websiteDomainNotConfigured' });
  }
  return blockers;
}

export function getImportCounts(state: WizardState) {
  const results = state.validation?.results ?? [];
  const valid = results.filter((result) => result.errors.length === 0).length;
  return { total: results.length, valid, invalid: results.length - valid };
}

/** The valid rows, as the backend receives them, with their original row number. */
export function getRowsToSend(
  state: WizardState
): CreateCampaignImportBatchDto['rows'] {
  const file = state.file;
  if (!file || !state.validation) return [];

  return state.validation.results.flatMap((result, index) =>
    result.errors.length === 0
      ? [{ rowNumber: file.rows[index].rowNumber, ...result.row }]
      : []
  );
}

/** Earliest and latest launch of the rows that will be sent (ISO strings), or null when none. */
export function getLaunchRange(
  state: WizardState
): { earliest: string; latest: string } | null {
  const launches = getRowsToSend(state)
    .map((row) => row.launchDateTime)
    .filter((launch) => launch !== '')
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  if (launches.length === 0) return null;
  return { earliest: launches[0], latest: launches[launches.length - 1] };
}

const PREVIOUS_STEP: Partial<Record<WizardStep, WizardStep>> = {
  review: 'mapping',
  confirm: 'review',
};

/** Actions that do not fit the current state return the same state object. */
export function importWizardReducer(
  state: WizardState,
  action: WizardAction
): WizardState {
  switch (action.type) {
    case 'fileLoaded': {
      if (state.step !== 'file') return state;

      const file: LoadedFile = {
        fileName: action.fileName,
        headers: action.headers,
        rows: action.rows,
      };
      const suggestions = suggestMapping(
        file.headers,
        file.rows.slice(0, SUGGESTION_SAMPLE_SIZE).map((row) => row.cells)
      );
      const mapping: ColumnMapping = Object.fromEntries(
        suggestions.map((suggestion) => [suggestion.field, suggestion.column])
      );
      return {
        ...createInitialWizardState(),
        step: 'mapping',
        file,
        suggestions,
        mapping,
        storeWebsiteDomain: action.storeWebsiteDomain,
        timezone: action.timezone,
        dateFormat: detectFormat(file, mapping),
      };
    }

    case 'mappingChanged': {
      if (state.step !== 'mapping' || !state.file) return state;
      const { field, column } = action;
      if (
        column !== null &&
        (!Number.isInteger(column) ||
          column < 0 ||
          column >= state.file.headers.length)
      ) {
        return state;
      }

      const mapping = { ...state.mapping };
      if (column === null) delete mapping[field];
      else mapping[field] = column;

      const redetects =
        !state.dateFormatForced &&
        (field === 'launchDate' || field === 'launchTime');
      return {
        ...state,
        mapping,
        dateFormat: redetects
          ? detectFormat(state.file, mapping)
          : state.dateFormat,
      };
    }

    case 'dateFormatChanged': {
      if (state.step !== 'mapping' && state.step !== 'review') return state;
      const next = {
        ...state,
        dateFormat: action.format,
        dateFormatForced: true,
      };
      return state.step === 'review' ? revalidateAll(next, action.now) : next;
    }

    case 'timezoneChanged': {
      if (state.step !== 'mapping' && state.step !== 'review') return state;
      const next = { ...state, timezone: action.timezone };
      return state.step === 'review' ? revalidateAll(next, action.now) : next;
    }

    case 'confirmMapping': {
      if (state.step !== 'mapping') return state;
      if (getMappingBlockers(state).length > 0) return state;
      return {
        ...revalidateAll(state, action.now),
        step: 'review',
      };
    }

    case 'cellEdited': {
      const { validation, file } = state;
      if (state.step !== 'review' || !validation || !file) return state;
      if (!file.rows[action.rowIndex]) return state;

      const edits = {
        ...state.edits,
        [action.rowIndex]: {
          ...state.edits[action.rowIndex],
          [action.field]: action.value,
        },
      };
      const values = rowValuesOf(
        file.rows[action.rowIndex],
        action.rowIndex,
        state.mapping,
        edits
      );
      return {
        ...state,
        edits,
        validation: revalidateImportRow(
          validation,
          action.rowIndex,
          values,
          contextOf(state, action.now)
        ),
      };
    }

    case 'skipInvalidChanged':
      if (state.step !== 'review') return state;
      return { ...state, skipInvalid: action.value };

    case 'advanceToConfirm': {
      if (state.step !== 'review') return state;
      const { valid, invalid } = getImportCounts(state);
      if (valid === 0 || (invalid > 0 && !state.skipInvalid)) return state;
      return { ...state, step: 'confirm' };
    }

    case 'goBack': {
      if (state.submission.status === 'sending') return state;
      if (state.step === 'mapping') return createInitialWizardState();

      const previous = PREVIOUS_STEP[state.step];
      if (!previous) return state;
      return { ...state, step: previous, submission: { status: 'idle' } };
    }

    case 'submitStarted':
      if (state.step !== 'confirm') return state;
      if (state.submission.status === 'sending') return state;
      return { ...state, submission: { status: 'sending' } };

    case 'submitSucceeded':
      if (state.submission.status !== 'sending') return state;
      return {
        ...state,
        step: 'result',
        submission: { status: 'done', result: action.result },
      };

    case 'submitFailed':
      if (state.submission.status !== 'sending') return state;
      return {
        ...state,
        submission: { status: 'failed', reason: action.reason },
      };

    case 'scheduleStarted':
      if (state.step !== 'result' || state.submission.status !== 'done') {
        return state;
      }
      if (
        state.schedule.status === 'sending' ||
        state.schedule.status === 'done'
      ) {
        return state;
      }
      return { ...state, schedule: { status: 'sending' } };

    case 'scheduleSucceeded':
      if (state.schedule.status !== 'sending') return state;
      return {
        ...state,
        schedule: { status: 'done', result: action.result },
      };

    case 'scheduleFailed':
      if (state.schedule.status !== 'sending') return state;
      return { ...state, schedule: { status: 'failed' } };

    case 'reset':
      return createInitialWizardState();
  }
}
